import { useEffect } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import AppLayout from '@/components/layout/AppLayout'
import LoginPage     from '@/pages/LoginPage'
import DashboardPage from '@/pages/DashboardPage'
import MapPage       from '@/pages/MapPage'
import ProductionPage from '@/pages/ProductionPage'
import DocumentsPage from '@/pages/DocumentsPage'
import ValidationPage from '@/pages/ValidationPage'
import ReportsPage   from '@/pages/ReportsPage'
import AdminPage     from '@/pages/AdminPage'
import LayoutPage    from '@/pages/LayoutPage'

// =============================================================================
// Protected Route wrapper
// =============================================================================

function ProtectedRoute({
  children,
  requiredRole,
}: {
  children: React.ReactNode
  requiredRole?: 'ADMIN' | 'SUPERVISOR_COBRA'
}) {
  const { user, isLoading, isAdmin, isSupervisor } = useAuth()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-brand-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-gray-500">Cargando...</p>
        </div>
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/login" replace />
  }

  if (requiredRole === 'ADMIN' && !isAdmin) {
    return <Navigate to="/" replace />
  }

  if (requiredRole === 'SUPERVISOR_COBRA' && !isSupervisor) {
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}

// =============================================================================
// Auth guard for login page (redirect if already logged in)
// =============================================================================

function GuestRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-10 h-10 border-4 border-brand-600 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  if (user) {
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}

// =============================================================================
// Main App
// =============================================================================

export default function App() {
  const location = useLocation()

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [location.pathname])

  return (
    <Routes>
      {/* Public */}
      <Route
        path="/login"
        element={
          <GuestRoute>
            <LoginPage />
          </GuestRoute>
        }
      />

      {/* Protected — all roles */}
      <Route
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="map"        element={<MapPage />} />
        <Route path="layout"     element={<LayoutPage />} />
        <Route path="production" element={<ProductionPage />} />
        <Route path="documents"  element={<DocumentsPage />} />
        <Route path="reports"    element={<ReportsPage />} />

        {/* Supervisor+ only */}
        <Route
          path="validation"
          element={
            <ProtectedRoute requiredRole="SUPERVISOR_COBRA">
              <ValidationPage />
            </ProtectedRoute>
          }
        />

        {/* Admin only */}
        <Route
          path="admin"
          element={
            <ProtectedRoute requiredRole="ADMIN">
              <AdminPage />
            </ProtectedRoute>
          }
        />
      </Route>

      {/* Catch-all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}
